import React from 'react';
import { Observe } from 'rewire-core';
import { WithStyle, FormView, IActionMenuItem } from 'rewire-ui';
import classNames from 'classnames';
import { Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Typography from '@material-ui/core/Typography';
import OptionsActionMenu from '../ui/OptionsActionMenu';
import Dialog from '../ui/Dialog';
import CustomFilterView from '../utility/CustomFilterView';
import scheduleRuleDialogModel from './ScheduleRuleDialogModel';

import { withStyles } from '../utility/styles';
import PrioritySortMenu from '../ui/PrioritySortMenu';
import SchedulingCandidateEmployeeGrid from './SchedulingCandidateEmployeeGrid';

const styles = (_theme: Theme) => ({
  contentContainer: {
    margin: '0.5rem 1rem 0.75rem 1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  infoContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 40%))',
    gridAutoFlow: 'dense',
    gridGap: '8px',
    width: '100%',
    marginBottom: '5px',
  },
  sortTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '5px',
  },
  sortTitle: {
    marginBottom: '0px !important',
  },
  headerTitle: {
    fontSize: '22px',
    marginBottom: '5px',
  },
  gridContainer: {
    display: 'flex',
    minHeight: '255px',
    maxHeight: '255px',
  },
  menuButton: {
    minWidth: '0px',
    minHeight: '0px',
    padding: '0px',
    marginLeft: '5px',
    '&:first-child': {
      marginLeft: '0px',
    },
  },
  dialogHeading: {
    alignItems: 'stretch',
  },
  dialogTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogTitleLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  lockIcon: {
    marginLeft: '10px',
  },
  scheduleRuleContextSearchContainer: {
    width: '335px',
    margin: '10px 0px',
  },
});

interface IScheduleRuleDialogProps {}

type ScheduleRuleDialogProps = WithStyle<ReturnType<typeof styles>, IScheduleRuleDialogProps>;

class ScheduleRuleDialog extends React.PureComponent<ScheduleRuleDialogProps> {
  optionsActionMenuItems: IActionMenuItem[];

  constructor(props: ScheduleRuleDialogProps) {
    super(props);

    this.optionsActionMenuItems = [
      {
        name: 'deleteSchedulingRule',
        title: 'Delete Scheduling Rule',
        icon: DeleteIcon,
        onClick: this.handleDeleteSchedulingRuleClick,
        closeOnClick: true,
        disabled: () => !scheduleRuleDialogModel.canDeleteRule(),
      },
    ];
  }

  componentWillUnmount() {
    scheduleRuleDialogModel.reset();
  }

  handleDeleteSchedulingRuleClick = () => {
    scheduleRuleDialogModel.deleteSchedulingRule();
  };

  renderTitle = (): JSX.Element => {
    const { classes } = this.props;

    return (
      <div className={classes.dialogTitleContainer}>
        <div className={classes.dialogTitleLabel}>
          {scheduleRuleDialogModel.scheduleRuleId ? (scheduleRuleDialogModel.viewOnly ? 'View' : 'Edit') : 'Add'}{' '}
          {scheduleRuleDialogModel.title}
        </div>
        {scheduleRuleDialogModel.scheduleRuleId && (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <OptionsActionMenu
              title="Scheduling Rule Options"
              tooltip="Scheduling Rule Options"
              items={this.optionsActionMenuItems}
            />
          </div>
        )}
      </div>
    );
  };

  renderScheduleRuleContextSearch = (): JSX.Element | null => {
    const { classes } = this.props;
    return !scheduleRuleDialogModel.hasStateScheduleRuleContext() && scheduleRuleDialogModel.scheduleRuleContextSearchComponent ? (
      <div className={classes.scheduleRuleContextSearchContainer}>
        <scheduleRuleDialogModel.scheduleRuleContextSearchComponent />
      </div>
    ) : null;
  };

  renderDialogContents = React.memo((): JSX.Element => {
    return (
      <Observe
        render={() => (
          <>
            <div className={this.props.classes.contentContainer}>
              <div className={this.props.classes.infoContainer}>
                <scheduleRuleDialogModel.form.field.scheduleRuleName.Editor className="span1" />
              </div>
              <div className={this.props.classes.sortTitleContainer}>
                <Typography className={classNames(this.props.classes.headerTitle, this.props.classes.sortTitle)}>Sorts</Typography>
                <PrioritySortMenu
                  model={scheduleRuleDialogModel.sortModel}
                  classes={{ menuButton: this.props.classes.menuButton }}
                />
              </div>
              <Typography>{scheduleRuleDialogModel.sortModel.getSortAsString(4)}</Typography>
            </div>
            <FormView form={scheduleRuleDialogModel.form} onSubmit={scheduleRuleDialogModel.actionFn('save')}>
              <CustomFilterView model={scheduleRuleDialogModel.customFilterModel} />
            </FormView>
            <div className={this.props.classes.contentContainer}>
              <Typography className={this.props.classes.headerTitle}>Candidate Employees List</Typography>
              <div className={this.props.classes.gridContainer}>
                <SchedulingCandidateEmployeeGrid
                  model={scheduleRuleDialogModel.schedulingGridModel}
                  GridHeading={this.renderScheduleRuleContextSearch}
                />
              </div>
            </div>
          </>
        )}
      />
    );
  });

  render() {
    return (
      <Observe
        render={() => (
          // <DraggableResizableDialog defaultWidth='sm' classes={{heading: this.props.classes.dialogHeading}} DialogProps={{dialog: scheduleRuleDialogModel, title: this.renderTitle, actions: ['cancel', 'reload schedule', 'save']}} DraggableResizableBoxProps={{minWidth: '924px', minHeight: '480px'}}>
          //   <this.renderDialogContents />
          // </DraggableResizableDialog>
          <Dialog
            dialog={scheduleRuleDialogModel}
            title={this.renderTitle}
            actions={['cancel', 'reload schedule', 'save']}
            maxWidth="md"
            classes={{ heading: this.props.classes.dialogHeading }}
          >
            <this.renderDialogContents />
          </Dialog>
        )}
      />
    );
  }
}

export default withStyles<IScheduleRuleDialogProps>(styles, ScheduleRuleDialog);
